import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
import RoleBasedGuard from 'src/auth/guard/role-based-guard';

import { LoadingScreen } from 'src/components/loading-screen';
import { RouteGuard, DashboardGuard } from 'src/components/permissions';

// ----------------------------------------------------------------------
const HomePage = lazy(() => import('src/pages/medicore/home'));
const CreateRoomPage = lazy(() => import('src/pages/medicore/rooms/new'));
const ListRoomPage = lazy(() => import('src/pages/medicore/rooms/list'));
const RoomDetailPage = lazy(() => import('src/pages/medicore/rooms/detail'));

const ListPatientInvoicePage = lazy(() => import('src/pages/medicore/patient-invoices/list'));
const CreatePatientInvoicePage = lazy(() => import('src/pages/medicore/patient-invoices/new'));
const HospitalizedEditorPage = lazy(() => import('src/pages/medicore/hospitalized/hospitalized'));
const IntensiveCareUnitEditorPage = lazy(
  () => import('src/pages/medicore/intensive-care-unit/icu')
);
const LaboratoryEditorPage = lazy(() => import('src/pages/medicore/laboratories/laboratories'));
const AdmissionRequestPage = lazy(
  () => import('src/pages/medicore/admission-requests/admission-requests')
);
const DeathsEditorPage = lazy(() => import('src/pages/medicore/deaths/deaths'));
const EjemploPage = lazy(() => import('src/pages/medicore/ejemplo/ejemplo'));
const AdmissionListView = lazy(() => import('src/pages/medicore/admissions/admissions-list-view'));
const AdmissionNewPage = lazy(() => import('src/pages/medicore/admissions/admission-new'));
const AdmissionDetailsView = lazy(
  () => import('src/pages/medicore/admissions/admissions-details-view')
);
const NurseShiftsPage = lazy(() => import('src/pages/medicore/nurse/shift-details-view'));
const NurseRequestProductsPage = lazy(
  () => import('src/pages/medicore/nurse/product-request-view')
);
const MedicalServiceCatalogPage = lazy(
  () => import('src/pages/medicore/medical-service-catalog/medical-service-page')
);

// ----------------------------------------------------------------------

/**
 * Componente wrapper para proteger rutas con permisos específicos
 * ⚠️ ULTRA-RESTRICTIVO: Solo master ve todo, otros usuarios solo lo que tienen permisos
 */
const ProtectedRoute = ({ route, children }) => (
  <RouteGuard route={route} fallbackRoute="/medicore" showFallback>
    <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
  </RouteGuard>
);

ProtectedRoute.propTypes = {
  route: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ----------------------------------------------------------------------

/**
 * Routes configuration for the Medicore section of the application.
 *
 * @constant {Array} medicoreRoutes - Array of route objects.
 * @property {string} path - The path for the route.
 * @property {JSX.Element} element - The component to render for the route.
 * @property {Array} children - Nested routes configuration.
 *
 * @example
 * // Example usage:
 * import { medicoreRoutes } from './path/to/medicore';
 *
 * <Router>
 *   {medicoreRoutes.map((route, index) => (
 *     <Route key={index} path={route.path} element={route.element}>
 *       {route.children && route.children.map((child, childIndex) => (
 *         <Route key={childIndex} path={child.path} element={child.element} />
 *       ))}
 *     </Route>
 *   ))}
 * </Router>
 */
export const medicoreRoutes = [
  {
    path: 'medicore',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['doctor', 'employee']}>
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      {
        element: (
          <DashboardGuard enableDebugLogs showLoadingWhileRedirecting>
            <Suspense fallback={<LoadingScreen />}>
              <HomePage />
            </Suspense>
          </DashboardGuard>
        ),
        index: true,
      },
      {
        path: 'ejemplo',
        element: (
          <ProtectedRoute route="/medicore/ejemplo">
            <EjemploPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admissions',
        element: (
          <ProtectedRoute route="/medicore/admissions">
            <AdmissionListView />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admissions/new',
        element: (
          <ProtectedRoute route="/medicore/admissions">
            <AdmissionNewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admissions/:id',
        element: (
          <ProtectedRoute route="/medicore/admissions">
            <AdmissionDetailsView />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rooms',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/rooms">
                <ListRoomPage />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/rooms/new">
                <CreateRoomPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/rooms/list">
                <ListRoomPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute route="/medicore/rooms/list">
                <RoomDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'medicalServiceCatalog',
        element: (
          <ProtectedRoute route="/medicore/medicalServiceCatalog">
            <MedicalServiceCatalogPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'patientInvoice',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/patientInvoice/list">
                <ListPatientInvoicePage />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/patientInvoice/new">
                <CreatePatientInvoicePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/patientInvoice/list">
                <ListPatientInvoicePage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'nurse',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/nurse/shifts">
                <NurseShiftsPage />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'shifts',
            element: (
              <ProtectedRoute route="/medicore/nurse/shifts">
                <NurseShiftsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'requestproducts',
            element: (
              <ProtectedRoute route="/medicore/nurse/requestproducts">
                <NurseRequestProductsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'hospitalized',
        element: (
          <ProtectedRoute route="/medicore/hospitalized">
            <HospitalizedEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'intensiveCareUnit',
        element: (
          <ProtectedRoute route="/medicore/intensiveCareUnit">
            <IntensiveCareUnitEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'laboratories',
        element: (
          <ProtectedRoute route="/medicore/laboratories">
            <LaboratoryEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'deaths',
        element: (
          <ProtectedRoute route="/medicore/deaths">
            <DeathsEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admissionRequests',
        element: (
          <ProtectedRoute route="/medicore/admissionRequests">
            <AdmissionRequestPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
];
